/* eslint-disable @typescript-eslint/no-explicit-any, complexity */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, shareReplay, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { API_URL, FCM_API_URL } from '../../services';
import { RequestHeader } from '../constants/headers';
import { CsrfTokenService } from '../services/csrfTokenService';
import { HubContextFacade } from '../state/administration';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | string;

@Injectable()
export class AuthCookieInterceptor implements HttpInterceptor {
  private reacquireInFlight$: Observable<boolean> | null = null;

  constructor(private csrf: CsrfTokenService, private http: HttpClient, private hubContextFacade: HubContextFacade) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // CSRF/XSRF for only API calls
    if (!this.isApiCall(request.url)) {
      return next.handle(request);
    }

    const isSkip = request.headers?.has(RequestHeader.SkipInterceptor);
    const isMutating = this.isMutatingMethod(request.method);

    let cloned = request.clone({ withCredentials: true });

    // Inject header only for mutating and not skipped
    if (isMutating && !isSkip) {
      const token = this.csrf.get();
      if (token) {
        cloned = cloned.clone({ setHeaders: { 'X-XSRF-TOKEN': token } });
      }
    }

    return next.handle(cloned).pipe(
      tap((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          this.trySaveMaskedTokenFromHeaders(event.headers);
          this.handleAnalyticsEvents(event, request);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        this.trySaveMaskedTokenFromHeaders(err.headers);

        const alreadyRetried = cloned.headers.has('X-CSRF-RETRY');
        const shouldTryReacquire = !alreadyRetried && isMutating && !isSkip && err.status === 403;

        if (!shouldTryReacquire) {
          return throwError(() => err);
        }

        return this.reacquireCsrfOnce().pipe(
          switchMap((ok: boolean) => {
            if (!ok) {
              return throwError(() => err);
            }

            const token = this.csrf.get();
            if (!token) {
              return throwError(() => err);
            }

            const retryReq = cloned.clone({
              setHeaders: {
                'X-XSRF-TOKEN': token,
                'X-CSRF-RETRY': '1',
              },
            });

            return next.handle(retryReq);
          }),
          catchError(() => throwError(() => err)),
        );
      }),
    );
  }

  private handleAnalyticsEvents(event: HttpResponse<unknown>, request: HttpRequest<unknown>): void {
    if (!event.url) {
      return;
    }

    const selectedHub = this.hubContextFacade.getSelectedHubSnapshot();
    let hubId: number;
    let hubName: string;
    if (selectedHub && selectedHub.id) {
      hubId = selectedHub.id;
      hubName = selectedHub.name;
    }

    if (event.url?.includes('/auth/me')) {
      const body = event.body as any;

      const userRole = body?.roles && body.roles.length > 0 ? body.roles.join(',') : undefined;

      // Push user roles to dataLayer for analytics purposes
      if (userRole) {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: 'user_data_loaded',
          user_role: userRole,
          hub_id: hubId,
          hub_name: hubName,
        });
      }
    }

    // Push event to dataLayer when ULI is created, with creation method
    if (event.url?.includes('/web-uli') && request.method === 'POST') {
      let creationMethod = 'unknown';

      if (event.url?.includes('/web-uli/multi-stop')) {
        creationMethod = 'multi_stop';
      } else if (event.url?.includes('/web-uli/create-allocate')) {
        const body = request.body as { strategy?: string };
        if (body && body.strategy === 'CALENDAR_BASED') {
          creationMethod = 'calendar_based';
        } else if (body && body.strategy === 'DEFAULT') {
          creationMethod = 'rule_based';
        }
      } else if (event.url?.endsWith('/web-uli')) {
        creationMethod = 'default';
      }

      if (creationMethod !== 'unknown') {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: 'uli_created',
          uli_creation_method: creationMethod,
          hub_id: hubId,
          hub_name: hubName,
        });
      }
    }

    // Push event to dataLayer when ULI is registered for analytics purposes
    if (
      (event.url?.includes('action/register') || event.url?.includes('action/register-st')) &&
      request.method === 'POST'
    ) {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'uli_registered',
        hub_id: hubId,
        hub_name: hubName,
      });
    }

    //Push event to dataLayer when ULI is created for analytics purposes
    if (event.url?.includes('/action/call-to-dock') && request.method === 'POST') {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'call_to_dock_action',
        hub_id: hubId,
        hub_name: hubName,
      });
    }

    // Push event to dataLayer when ULI is allocated for analytics purposes, with allocation method (manual, auto, rule-based)
    if (event.url?.includes('/action/allocate/') && request.method === 'POST') {
      let allocationMethod = 'unknown';
      if (event.url.includes('/manual')) {
        allocationMethod = 'manual';
      } else if (event.url.includes('/auto')) {
        allocationMethod = 'auto';
      } else if (event.url.includes('/by-rule')) {
        allocationMethod = 'rule_based';
      }

      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'uli_allocated',
        allocation_method: allocationMethod,
        hub_id: hubId,
        hub_name: hubName,
      });
    }

    if (event.url?.includes('/action/register/prepare') && request.method === 'POST') {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({ event: 'uli_registration_started', hub_id: hubId, hub_name: hubName });
    }
  }

  private reacquireCsrfOnce(): Observable<boolean> {
    // If one is already running, reuse it
    if (this.reacquireInFlight$) {
      return this.reacquireInFlight$;
    }

    // Use /auth/me only to reacquire the latest masked CSRF token.
    const base = (API_URL || '').replace(/\/+$/, '');
    const url = `${base}/auth/me`;

    const headers = new HttpHeaders().set(RequestHeader.SkipInterceptor, '1');

    this.reacquireInFlight$ = this.http.get(url, { withCredentials: true, observe: 'response', headers }).pipe(
      tap((resp: HttpResponse<unknown>) => {
        this.trySaveMaskedTokenFromHeaders(resp.headers);
      }),
      map(() => this.csrf.hasToken()),
      catchError(() => of(false)),
      tap(() => {
        // clear lock after completion (success or failure)
        this.reacquireInFlight$ = null;
      }),
      shareReplay(1),
    );

    return this.reacquireInFlight$;
  }

  private trySaveMaskedTokenFromHeaders(headers: { get(name: string): string | null } | null | undefined): boolean {
    if (!headers) {
      return false;
    }
    const masked = headers.get('x-xsrf-token') ?? headers.get('X-XSRF-TOKEN');
    if (masked) {
      this.csrf.set(masked);
      return true;
    }
    return false;
  }

  private isMutatingMethod(method: HttpMethod): boolean {
    return method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
  }

  private isApiCall(url: string): boolean {
    if (url.startsWith('/api/') || url.startsWith('/fcm/')) {
      return true;
    }

    const base = (API_URL || '').replace(/\/+$/, '');
    const fcmBase = (FCM_API_URL || '').replace(/\/+$/, '');

    return (!!base && url.startsWith(base)) || (!!fcmBase && url.startsWith(fcmBase));
  }
}
